import React, { useEffect } from 'react';

import cn from 'classnames';
import { twMerge } from 'tailwind-merge';

import { ClickOutside } from '@components/ClickOutside';
import { Portal } from '@components/Portal';
import { CloseSVG } from '@components/svgs';
import { useOnEscape } from '@components/utils';
import { createPolymorphicComponent } from '@helpers/react/createPolymorphicComponent';

import { SlideOutCloseButton } from './components/SlideOutCloseButton';
import { SlideOutMask } from './components/SlideOutMask';
import { SlideOutProvider } from './context/SlideOutContext';
import * as Types from './types';

export const SlideOut = createPolymorphicComponent<Types.Props, 'section'>(
  ({
    as: Component = 'section',
    children,
    className,
    hasMask,
    hideCloseButton,
    innerRef,
    isOpen,
    shouldLockBackground = true,
    width,
    onClose,
    ...rest
  }) => {
    const onCloseWithReason = () => {
      onClose?.('clickOutside');
    };

    useOnEscape(() => {
      onClose?.('esc');
    });

    useEffect(() => {
      if (isOpen && shouldLockBackground) {
        document.body.classList.add('overflow-hidden');
      } else {
        document.body.classList.remove('overflow-hidden');
      }

      return () => {
        document.body.classList.remove('overflow-hidden');
      };
    }, [isOpen, shouldLockBackground]);

    return (
      <SlideOutProvider value={{ isOpen, onClose }}>
        <Portal>
          <>
            {hasMask && <SlideOutMask />}

            <ClickOutside isDisabled={hasMask || !isOpen} onClick={onCloseWithReason}>
              <Component
                ref={innerRef}
                data-testid='slide-out'
                className={twMerge(
                  cn(
                    'fixed right-0 top-0 z-20 h-full w-1/2 transform-gpu overflow-y-auto bg-white shadow-lg transition-transform duration-150 ease-in-out',
                    { 'pointer-events-none translate-x-full shadow-none': !isOpen }
                  ),
                  className
                )}
                style={{ width }}
                {...rest}
              >
                {!hideCloseButton && (
                  <SlideOutCloseButton
                    className='absolute right-4 top-4 text-gray-500 hover:text-indigo-600'
                    aria-label='Close'
                  >
                    <CloseSVG className='h-4 w-4' />
                  </SlideOutCloseButton>
                )}

                {children}
              </Component>
            </ClickOutside>
          </>
        </Portal>
      </SlideOutProvider>
    );
  }
);
